import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useHistory } from 'react-router-dom'

import { Button, Col, Form, Input, Row, SelectPicker, Toggle, Panel, DatePicker, Table, InputNumber, Tag } from 'rsuite'

import ShopTopMenu from '@components/shop/ShopTopMenu'
import { useFetchSingleStorageContainer } from '@@partner:graphql/query/storageContainer/singleStorageContainer.gql'
import { useUpdateStorageContainer } from '@@partner:graphql/mutation/storageContainer/updateStorageContainer.gql'
import { StorageContainer } from '@@partner:types:app'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'
import { generatePath } from 'react-router-dom'
import { ROUTE_STORAGE_CONTAINERS } from '@@partner:config/routes'
import { CONTAINER_SIZES, CONTAINER_TYPES } from '../../constants/storageContainer'
import AppLoader from '@components/common/AppLoader'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import { ArrowBack } from 'react-ionicons'
import { mergeLeft } from 'merge-left-utils'
import { 
  StorageContainerFormState, 
  makeStorageContainerFormState, 
  mapApiDataToState,
  mapStateToApiVariable
} from './StorageContainerFormState'
import { StorageContainerFormValidationModel } from './validation/StorageContainerFormValidationModel'
import { notifyOk, notifyError, makeGraphQLErrorNotification } from '@@partner:helpers/notification'
import FormTextareaControl from '@components/common/form/FormTextareaControl'

export default function EditStorageContainerPage () {
  const { brandSlug, containerId } = useParams<any>()
  const history = useHistory()
  
  const { formState, setFormState, setFormRef } = useValidatableForm<StorageContainerFormState>(
    makeStorageContainerFormState
  )
  
  const { data, loading: loadingContainer, error } = useFetchSingleStorageContainer({
    id: containerId
  })

  const [ updateContainer, { loading: updating } ] = useUpdateStorageContainer()
  
  const sizeOptions = CONTAINER_SIZES
  const typeOptions = CONTAINER_TYPES

  useEffect(() => {
    if (!loadingContainer && data?.singleStorageContainer) {
      setFormState((state) => 
        mergeLeft(state, mapApiDataToState(data.singleStorageContainer))
      )
    }
  }, [ data, loadingContainer, setFormState ])

  const handleFormChange = useCallback(
    (formData: any) => {
      setFormState((state) => mergeLeft(state, formData))
    },
    [ setFormState ]
  )

  const handleDateChange = useCallback(
    (value: Date | null) => {
      setFormState((state) => 
        mergeLeft(state, { next_sanitize_at: value ? value.getTime() : undefined })
      )
    },
    [ setFormState ]
  )

  const handleFormSubmit = useCallback(
    (isOk = false) => {
      if (isOk) {
        updateContainer({
          variables: {
            id: containerId,
            input: mapStateToApiVariable(formState)
          }
        })
          .then(() => {
            notifyOk('Контейнер успешно сохранен')
            history.push(generatePath(ROUTE_STORAGE_CONTAINERS, { brandSlug }))
          })
          .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
      } 
      else {
        notifyError('Форма заполнена некорректно')
      }
    },
    [ formState, containerId, updateContainer, history, brandSlug ]
  )

  if (loadingContainer) {
    return <AppLoader />
  }

  if (error) {
    return <GraphQLCommonErrorDisplay error={error} />
  }

  if (!data?.singleStorageContainer) {
    return <div>Контейнер не найден</div>
  }

  const container = data.singleStorageContainer

  return (
    <div>
      <ShopTopMenu />
      <div className='my-5 flex flex-row align-middle gap-2'>
        <Button>
          <ArrowBack
            style={{ cursor: 'pointer' }}
            onClick={() => history.goBack()}
          />
          <span>Вернуться назад</span>
        </Button>
      </div>
      <Panel 
        shaded 
        className='my-5' 
        header={`Редактирование контейнера: ${container.displayName}`} 
      >
        <Form
          formValue={formState}
          onChange={handleFormChange}
          onSubmit={handleFormSubmit}
          ref={setFormRef}
          model={StorageContainerFormValidationModel}
          fluid>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Group>
                <Form.ControlLabel>Код контейнера *</Form.ControlLabel>
                <Form.Control 
                  name="container_id"
                  placeholder="Введите уникальный код..."
                />
                <Form.HelpText>
                  Уникальный идентификатор контейнера (штрих-код, QR-код)
                </Form.HelpText>
              </Form.Group>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Group>
                <Form.ControlLabel>Размер *</Form.ControlLabel>
                <Form.Control 
                  name="size"
                  accepter={SelectPicker}
                  data={sizeOptions}
                  placeholder="Выберите размер"
                  searchable={false}
                  cleanable={false}
                  style={{ width: '100%' }}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Group>
                <Form.ControlLabel>Тип *</Form.ControlLabel>
                <Form.Control 
                  name="type"
                  accepter={SelectPicker}
                  data={typeOptions}
                  placeholder="Выберите тип"
                  searchable={false}
                  cleanable={false}
                  style={{ width: '100%' }}
                />
              </Form.Group>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Group>
                <Form.ControlLabel>Активен</Form.ControlLabel>
                <Form.Control 
                  name="is_active"
                  accepter={Toggle}
                />
                <Form.HelpText>
                  Контейнер будет доступен для использования
                </Form.HelpText>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col xs={24}>
              <Form.Group>
                <Form.ControlLabel>Описание</Form.ControlLabel>
                <FormTextareaControl
                  placeholder='Описание'
                  name='Дополнительное описание контейнера...'
                  rows={5}
                />
              </Form.Group>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Group>
                <Form.ControlLabel>Дата следующей санобработки</Form.ControlLabel>
                <DatePicker
                  value={formState.next_sanitize_at ? new Date(formState.next_sanitize_at) : null}
                  onChange={handleDateChange}
                  placeholder="Выберите дату..."
                  style={{ width: '100%' }}
                  format="dd.MM.yyyy"
                />
                <Form.HelpText>
                  Когда требуется следующая санитарная обработка
                </Form.HelpText>
              </Form.Group>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Group>
                <Form.ControlLabel>Интервал санобработки (дней)</Form.ControlLabel>
                <Form.Control
                  name="sanitization_interval_days"
                  accepter={InputNumber}
                  placeholder="Введите количество дней..."
                  min={1}
                  style={{ width: '100%' }}
                />
                <Form.HelpText>
                  Периодичность проведения санитарной обработки
                </Form.HelpText>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col xs={24}>
              <div style={{ marginBottom: 16 }}>
                <strong>Информация:</strong>
                <ul>
                  <li>Создан: {new Date(container.created_at).toLocaleString('ru-RU')}</li>
                  <li>Обновлен: {new Date(container.updated_at).toLocaleString('ru-RU')}</li>
                  <li>Всего заказов: {container.ordersCount || 0}</li>
                  <li>Активные заказы: {container.activeOrdersCount || 0}</li>
                </ul>
              </div>
            </Col>
          </Row>

          {container.orderContainers && container.orderContainers.length > 0 && (
            <Row className="mt-4">
              <Col xs={24}>
                <Panel header="Связанные заказы" collapsible defaultExpanded>
                  <Table
                    data={container.orderContainers}
                    autoHeight
                  >
                    <Table.Column width={100}>
                      <Table.HeaderCell>ID заказа</Table.HeaderCell>
                      <Table.Cell dataKey="order_id" />
                    </Table.Column>
                    <Table.Column width={150}>
                      <Table.HeaderCell>Статус</Table.HeaderCell>
                      <Table.Cell>
                        {(rowData) => {
                          const isPacked = rowData.isPacked
                          const isUnpacked = rowData.isUnpacked
                          
                          if (isPacked && !isUnpacked) {
                            return <Tag color="green">Упакован</Tag>
                          }
                          else if (isPacked && isUnpacked) {
                            return <Tag color="blue">Распакован</Tag>
                          }
                          else {
                            return <Tag color="orange">Ожидает</Tag>
                          }
                        }}
                      </Table.Cell>
                    </Table.Column>
                    <Table.Column width={200}>
                      <Table.HeaderCell>Дата упаковки</Table.HeaderCell>
                      <Table.Cell>
                        {(rowData) => rowData.packed_at 
                          ? new Date(rowData.packed_at).toLocaleString('ru-RU') 
                          : '-'
                        }
                      </Table.Cell>
                    </Table.Column>
                    <Table.Column width={200}>
                      <Table.HeaderCell>Дата распаковки</Table.HeaderCell>
                      <Table.Cell>
                        {(rowData) => rowData.unpacked_at 
                          ? new Date(rowData.unpacked_at).toLocaleString('ru-RU') 
                          : '-'
                        }
                      </Table.Cell>
                    </Table.Column>
                    <Table.Column width={300} flexGrow={1}>
                      <Table.HeaderCell>Примечания</Table.HeaderCell>
                      <Table.Cell>
                        {(rowData) => {
                          const notes = []
                          if (rowData.packing_notes) notes.push(`Упаковка: ${rowData.packing_notes}`)
                          if (rowData.unpacking_notes) notes.push(`Распаковка: ${rowData.unpacking_notes}`)
                          return notes.join('; ') || '-'
                        }}
                      </Table.Cell>
                    </Table.Column>
                  </Table>
                </Panel>
              </Col>
            </Row>
          )}

          <Row className="mt-4">
            <Col xs={24}>
              <Form.Group>
                <Button
                  appearance="primary"
                  type="submit"
                  loading={updating}
                  disabled={updating || !formState.container_id || !formState.size || !formState.type}
                >
                  Сохранить изменения
                </Button>
                <Button
                  appearance="subtle"
                  onClick={() => history.goBack()}
                  style={{ marginLeft: 8 }}
                >
                  Отмена
                </Button>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Panel>
    </div>
  )
}
